import { memo, useMemo } from 'react';
import cn from 'bem-cn';

import { Stack } from '../UI';
import { useGetSlidesQuery } from '../../store';

import { Slide, SlideSkeleton } from './Slide';
import { SlidesControls } from './SlidesControls';
import { SlidesDots } from './SlidesDots';
import { useEmbla } from './Slides.hooks';
import { SlidesProps } from './Slides.types';
import './Slides.scss';

const b = cn('slides');

export const Slides = memo((props: SlidesProps) => {
    const { isTouch } = props;

    const { data: slides = [], isFetching, isError } = useGetSlidesQuery();

    // Мемоизируем bgColors чтобы useEmblaGradient не переподписывался на события
    // embla при каждом рендере (например при изменении activeIndex)
    const bgColors = useMemo(() => slides.map(slide => slide.backgroundColor), [slides]);

    const { emblaRef, count, activeIndex, handlePrev, handleNext } = useEmbla(bgColors);

    // Мемоизируем controls чтобы не пересоздавать JSX при каждом рендере (activeIndex)
    const controls = useMemo(() => {
        if (isTouch) {
            return null;
        }

        return <SlidesControls onPrev={handlePrev} onNext={handleNext} />;
    }, [isTouch, handlePrev, handleNext]);

    if (isFetching) {
        return (
            <Stack className={b()}>
                <SlideSkeleton />
            </Stack>
        );
    }

    if (isError || !slides.length) {
        return <div className={b('error')} />;
    }

    return (
        <Stack className={b()}>
            <div ref={emblaRef} className={b('viewport')}>
                <div className={b('container')}>
                    {slides.map(slide => (
                        <Slide
                            key={slide.id}
                            className={b('slide')}
                            slide={slide}
                            isTouch={isTouch}
                            controls={controls}
                        />
                    ))}
                </div>
                <SlidesDots className={b('dots')} count={count} activeIndex={activeIndex} />
            </div>
        </Stack>
    );
});

Slides.displayName = 'Slides';
